import { atom } from 'jotai';

/**
 * Represents a loading operation tracked in the global loading registry
 */
export type LoadingOperation = {
  /** Unique identifier for the operation */
  id: string;
  /** Optional human-readable label for debugging */
  label?: string;
  /** Timestamp when the operation started */
  timestamp: number;
  /** Whether this operation should trigger the global loading overlay */
  showOverlay: boolean;
};

/**
 * 🔘 PLAIN ATOM - Map of all active loading operations
 * Key: operation ID
 * Value: LoadingOperation details
 */
export const $loadingOperations = atom<Map<string, LoadingOperation>>(
  new Map(),
);

/**
 * 🟢 COMPUTED - Whether the global loading overlay should be visible
 * Returns true if at least one operation has showOverlay: true
 */
export const $isGloballyLoading = atom((get) => {
  const operations = get($loadingOperations);
  // Only show overlay if there's at least one operation with showOverlay: true
  return Array.from(operations.values()).some((op) => op.showOverlay);
});

/**
 * 🟠 ACTION - Register a new loading operation
 */
export const $registerLoading = atom(
  null,
  (get, set, operation: LoadingOperation) => {
    const ops = new Map(get($loadingOperations));
    ops.set(operation.id, operation);
    set($loadingOperations, ops);
  },
);

/**
 * 🟠 ACTION - Unregister a loading operation
 */
export const $unregisterLoading = atom(null, (get, set, id: string) => {
  const ops = new Map(get($loadingOperations));
  ops.delete(id);
  set($loadingOperations, ops);
});

/**
 * 🟢 COMPUTED - Get count of active loading operations (for debugging)
 */
export const $loadingOperationsCount = atom((get) => {
  const operations = get($loadingOperations);
  return operations.size;
});

/**
 * 🟢 COMPUTED - Get count of operations that show overlay (for debugging)
 */
export const $overlayOperationsCount = atom((get) => {
  const operations = get($loadingOperations);
  return Array.from(operations.values()).filter((op) => op.showOverlay).length;
});
